import { config as commonConfig } from '@genially/commons';

import { DEFAULT_LANGUAGE, getSavedOrBrowserLanguage } from '../utils/language';
import {
  AppEnv,
  getAllEnvConfigs,
  getCurrentAppEnv,
  getCurrentAppEnvConfig,
  isProductionEnv,
} from './appEnv';

const inBrowser = typeof window !== 'undefined';
// assume we are in production when used in server (e.g. view SSR)
const getHref = () => (inBrowser ? window.location.href : 'https://app.genially.com/');

const SERVICE_WORKER_DEVELOPMENT = `${process.env.PUBLIC_URL}/service-worker-genially.js`;
const SERVICE_WORKER_PRODUCTION = '/editor/service-worker-genially.js';

const AUDIO_BUCKET_PRODUCTION = 'genially-audio';
const AUDIO_BUCKET_DEVELOPMENT = 'genially-audio-dev';

const getLocalizedBaseUrl = (baseUrl: string, forcedLocale?: string): string => {
  const language = forcedLocale || getSavedOrBrowserLanguage();
  return language === DEFAULT_LANGUAGE ? baseUrl : `${baseUrl}/${language}`;
};

export const generateConfig = (location: string, dynamicBranchName?: string) => {
  const locationUrl = new URL(location);
  const { searchParams } = locationUrl;

  const appEnv = getCurrentAppEnv(location);
  const appEnvConfig = getCurrentAppEnvConfig(location, dynamicBranchName);
  const appUrls = appEnvConfig.urls;
  const appKeys = appEnvConfig.keys;

  const localizedAuth = getLocalizedBaseUrl(appUrls.auth);
  const localizedAuthUS = appUrls.authUS
    ? getLocalizedBaseUrl(appUrls.authUS)
    : undefined;

  const isDebug = appEnv === AppEnv.Local;

  return {
    ...commonConfig,

    appEnv,
    appEnvConfig,
    appUrls,
    appKeys,

    get prod(): boolean {
      return process.env.NODE_ENV === 'production';
    },

    debug: isDebug,

    facebookAppId: appKeys.facebookAppId,
    analyticsId: appKeys.googleAnalyticsId,
    previewURL: appUrls.preview,
    embedlyKEY: appKeys.embedlyApiKey,
    iframelyAPIKEY: appKeys.iframelyApiKey,

    get loginBack(): string {
      return `${localizedAuth}/login?backTo=${encodeURIComponent(getHref())}`;
    },
    sessionLostUrl: `${localizedAuth}/session-lost`,
    onboardingUrl: `${localizedAuth}/onboarding`,
    onboardingUSUrl: localizedAuthUS ? `${localizedAuthUS}/onboarding` : undefined,
    migrationAgeConfirmationUrl: `${localizedAuth}/migration-age-confirmation`,
    paymentApiUrl: appUrls.paymentsApi,
    getNewApiURL: appUrls.newApi,
    migrationsApiUrl: appUrls.migrationsApi,
    wsServerURL: appUrls.wsServer,

    apiHealthCheckURL: isDebug
      ? `${appUrls.newApi}/api/health-check`
      : 'https://lc3eo0h9gl.execute-api.us-east-1.amazonaws.com/pro/status',

    get serviceWorkerUrl(): string {
      if (this.debug) {
        return SERVICE_WORKER_DEVELOPMENT;
      }

      return SERVICE_WORKER_PRODUCTION;
    },

    get verboseUndoManager(): boolean {
      return searchParams.has('debugUndo') || (this.verbose.undoManager && this.debug);
    },
    get verboseUndoManagerInsane(): boolean {
      return (
        searchParams.has('debugUndoInsane') ||
        (this.verbose.undoManagerInsane && this.debug)
      );
    },
    get verboseLogService(): boolean {
      return searchParams.has('debugLog') || (this.verbose.logService && this.debug);
    },

    processViewURL(friendlyURL: string): string {
      const replaceFrom = getAllEnvConfigs({ location })[AppEnv.Production].urls.view;
      const replaceTo = appUrls.view;

      return friendlyURL.replace(replaceFrom, replaceTo);
    },

    firebaseConfig: appKeys.firebase,
    CANVA_MAX_SIZE: 12000,
    CANVA_MIN_SIZE: 100,
    froalaKey: appKeys.froalaApiKey,
    limitUndoManager: 100,
    verbose: {
      undoManager: true,
      undoManagerInsane: false,
      logService: true,
    },

    get audioBucket(): string {
      if (this.prod && this.appEnv !== AppEnv.Dynamic) {
        return AUDIO_BUCKET_PRODUCTION;
      }
      return AUDIO_BUCKET_DEVELOPMENT;
    },

    // NEW PART PANEL

    // Si el entorno es desarrollo o el entorno es producción pero el host es app-staging.genially.com
    // entonces consideramos que estamos haciendo pruebas
    get isTesting(): boolean {
      return (
        process.env.NODE_ENV === 'development' || (this.prod && !appEnvConfig.production)
      );
    },

    get isProduction(): boolean {
      return isProductionEnv(this.appEnv);
    },

    pptImporterUrl: appUrls.pptImporter,
    pdfImporterUrl: appUrls.pdfImporter,
    gslidesImporterUrl: appUrls.gslidesImporter,
    editorUrl: appUrls.editor,
    urlPanel: appUrls.panel,
    urlView: appUrls.view,
    getApiURL: appUrls.newApi,
    urlBaseAuth: appUrls.auth,
    urlAuth: localizedAuth,
    urlAuthUS: localizedAuthUS,
    urlApiAuth: `${appUrls.auth}/api`,
    elearningDownloaderUrl: appUrls.elearningDownloader,
    elearningApiUrl: appUrls.elearningApi,
    trackingUrl: appUrls.tracking,
    playUrl: appUrls.play,
    downloadHtmlUrl: appUrls.downloadGeniallyAsHTML,
    urlLambdaCompress: appUrls.compressLambda,
    screenshotterUrl: appUrls.screenshotterUrl,
    newScreenshotterUrl: appUrls.newScreenshotterUrl,
    urlLambdaSaveBackup: appUrls.saveBackupLambda,
    urlMetricsServer: appUrls.metricsServer,
    getViewPrintURL: appUrls.viewPrint,
    getResourcesURL: appUrls.resources,
    getSlsUtilsURL: appUrls.slsUtils,
    typeformUrl: appUrls.typeform,
    typeformIds: appKeys.typeformIds,
    getWebUrl(language?: string): string {
      return getLocalizedBaseUrl(appUrls.web, language);
    },
    helpCenterLinks: appUrls.helpCenterLinks,
    cannyFeedbackLinks: appUrls.cannyFeedbackLinks,
    analyticsSupportLinks: appUrls.analyticsSupportLinks,
    userTrackingSupportLinks: appUrls.userTrackingSupportLinks,
    interactiveQuestionsSupportLinks: appUrls.interactiveQuestionsSupportLinks,
    storageSpaceSupportLinks: appUrls.storageSpaceSupportLinks,
    playSupportLinks: appUrls.playSupportLinks,
    ltiSupportLinks: appUrls.ltiSupportLinks,
    canvaIntegration: appUrls.canvaIntegration,
    geniusCentersUrls: {
      es: `${appUrls.web}/es/centros-geniales/`,
      en: `${appUrls.web}/genius-centers/`,
      fr: `${appUrls.web}/fr/etablissements-geniaux/`,
      ptBr: `${appUrls.web}/pt-br/centros-geniais/`,
      it: `${appUrls.web}/it/centri-geniali/`,
      de: `${appUrls.web}/de/genius-center/`,
    },
    termsAndConditionsUrls: {
      es: `${appUrls.web}/es/terminos-uso/`,
      en: `${appUrls.web}/terms/`,
      fr: `${appUrls.web}/fr/conditions-utilisation/`,
      ptBr: `${appUrls.web}/pt-br/termos-uso/`,
      it: `${appUrls.web}/it/condizioni-uso/`,
      de: `${appUrls.web}/de/geschaeftsbedingungen/`,
    },
    privacyUrls: {
      es: `${appUrls.web}/es/privacidad/`,
      en: `${appUrls.web}/privacy/`,
      fr: `${appUrls.web}/fr/confidentialite/`,
      ptBr: `${appUrls.web}/pt-br/privacidade/`,
      it: `${appUrls.web}/it/privacy/`,
      de: `${appUrls.web}/de/datenschutz/`,
    },
    cookiesUrls: {
      es: `${appUrls.web}/es/cookies/`,
      en: `${appUrls.web}/cookies/`,
      fr: `${appUrls.web}/fr/cookies/`,
      ptBr: `${appUrls.web}/pt-br/cookies/`,
      it: `${appUrls.web}/it/cookies/`,
      de: `${appUrls.web}/de/cookies/`,
    },
    legalNoticeUrls: {
      es: `${appUrls.web}/es/aviso-legal/`,
      en: `${appUrls.web}/legal-notice/`,
      fr: `${appUrls.web}/fr/mentions-legales/`,
      ptBr: `${appUrls.web}/pt-br/aviso-legal/`,
      it: `${appUrls.web}/it/avviso-legale/`,
      de: `${appUrls.web}/de/impressum/`,
    },
    plansUrls: {
      es: `${appUrls.web}/es/planes/`,
      en: `${appUrls.web}/plans/`,
      fr: `${appUrls.web}/fr/plans/`,
      ptBr: `${appUrls.web}/pt-br/planos/`,
      it: `${appUrls.web}/it/piani/`,
      de: `${appUrls.web}/de/preismodelle/`,
    },
    contactUrls: {
      es: `${appUrls.web}/es/contacto/`,
      en: `${appUrls.web}/contact/`,
      fr: `${appUrls.web}/fr/contact/`,
      ptBr: `${appUrls.web}/pt-br/fale-conosco/`,
      it: `${appUrls.web}/it/contatti/`,
      de: `${appUrls.web}/de/kontakt/`,
    },
    whyGeniallyUrls: {
      es: `${appUrls.web}/es/por-que-genially/`,
      en: `${appUrls.web}/why-genially/`,
      fr: `${appUrls.web}/fr/pourquoi-genially/`,
      ptBr: `${appUrls.web}/pt-br/porque-genially/`,
      it: `${appUrls.web}/it/perche-genially/`,
      de: `${appUrls.web}/de/warum-genially/`,
    },
    templatesUrls: {
      es: `${appUrls.web}/es/plantillas/`,
      en: `${appUrls.web}/templates/`,
      fr: `${appUrls.web}/fr/modeles/`,
      ptBr: `${appUrls.web}/pt-br/modelos/`,
      it: `${appUrls.web}/it/templates/`,
      de: `${appUrls.web}/de/vorlagen/`,
    },
    firstStepsUrls: {
      es: `${appUrls.web}/es/primeros-pasos/`,
      en: `${appUrls.web}/first-steps/`,
      fr: `${appUrls.web}/fr/premiers-pas/`,
      ptBr: `${appUrls.web}/pt-br/primeiros-passos/`,
      it: `${appUrls.web}/it/primi-passi/`,
      de: `${appUrls.web}/de/erste-schritte/`,
    },
    productTourUrls: {
      es: `${appUrls.web}/es/genially-tour/`,
      en: `${appUrls.web}/product-tour/`,
      fr: `${appUrls.web}/fr/genially-tour/`,
      ptBr: `${appUrls.web}/pt-br/genially-tour/`,
      it: `${appUrls.web}/it/genially-tour/`,
      de: `${appUrls.web}/de/das-ist-genially/`,
    },
    corporateUrls: {
      es: `${appUrls.web}/es/para-empresas/`,
      en: `${appUrls.web}/corporate/`,
      fr: `${appUrls.web}/fr/pour-entreprises/`,
      ptBr: `${appUrls.web}/pt-br/para-empresas/`,
      it: `${appUrls.web}/it/per-aziende/`,
      de: `${appUrls.web}/de/unternehmen/`,
    },
    educationUrls: {
      es: `${appUrls.web}/es/para-educacion/`,
      en: `${appUrls.web}/education/`,
      fr: `${appUrls.web}/fr/pour-education/`,
      ptBr: `${appUrls.web}/pt-br/educacao/`,
      it: `${appUrls.web}/it/per-educazione/`,
      de: `${appUrls.web}/de/bildung/`,
    },
    universityUrls: {
      es: `${appUrls.web}/es/para-universidad/`,
      en: `${appUrls.web}/university/`,
      fr: `${appUrls.web}/fr/pour-universite/`,
      ptBr: `${appUrls.web}/pt-br/para-universidades/`,
      it: `${appUrls.web}/it/per-universita/`,
      de: `${appUrls.web}/de/universitaet/`,
    },
    forDesignersUrls: {
      es: `${appUrls.web}/es/para-disenadores/`,
      en: `${appUrls.web}/for-designers/`,
      fr: `${appUrls.web}/fr/pour-designers/`,
      ptBr: `${appUrls.web}/pt-br/para-designers/`,
      it: `${appUrls.web}/it/per-designers/`,
      de: `${appUrls.web}/de/design/`,
    },
    weAreUrls: {
      es: `${appUrls.web}/es/quienes-somos/`,
      en: `${appUrls.web}/we-are/`,
      fr: `${appUrls.web}/fr/qui-sommes-nous/`,
      ptBr: `${appUrls.web}/pt-br/quem-somos/`,
      it: `${appUrls.web}/it/chi-siamo/`,
      de: `${appUrls.web}/de/wer-wir-sind/`,
    },
    weAreHiringUrls: {
      es: `${appUrls.web}/es/unete-al-equipo/`,
      en: `${appUrls.web}/we-are-hiring/`,
      fr: `${appUrls.web}/fr/rejoignez-equipe/`,
      ptBr: `${appUrls.web}/pt-br/junte-se-a-nos/`,
      it: `${appUrls.web}/it/unisciti-a-noi/`,
      de: `${appUrls.web}/de/werde-teil-des-teams/`,
    },
    blogUrls: appUrls.blog,
    academyUrls: appUrls.academy,
    systemStatusUrls: appUrls.systemStatus,
    signupUrls: {
      es: `${appUrls.auth}/es/signup/`,
      en: `${appUrls.auth}/signup/`,
      fr: `${appUrls.auth}/fr/signup/`,
      ptBr: `${appUrls.auth}/pt-br/signup/`,
      it: `${appUrls.auth}/it/signup/`,
      de: `${appUrls.auth}/de/signup/`,
    },
    facebookUrls: {
      es: 'https://www.facebook.com/Genially/',
      en: 'https://www.facebook.com/Geniallyofficial/',
      fr: 'https://www.facebook.com/GeniallyFrance/',
      ptBr: 'https://www.facebook.com/Geniallyofficial/', // TODO: pt-br change
      it: 'https://www.facebook.com/Geniallyofficial/', // TODO: IT change
      de: 'https://www.facebook.com/Geniallyofficial/', // TODO: DE change
    },
    twitterUrls: {
      es: 'https://x.com/genially_es/',
      en: 'https://x.com/genially_en/',
      fr: 'https://x.com/genially_fr/',
      ptBr: 'https://x.com/genially_en/', // TODO: pt-br change
      it: 'https://x.com/genially_en/', // TODO: IT change
      de: 'https://x.com/genially_en/', // TODO: DE change
    },
    socialConnect: {
      facebook: `${localizedAuth}/login/facebook`,
      google: `${localizedAuth}/login/google`,
      twitter: `${localizedAuth}/login/twitter`,
      linkedin: `${localizedAuth}/login/linkedin`,
      office365: `${localizedAuth}/login/office365`,
    },

    ALL_VIEW_URLS: Object.values(getAllEnvConfigs({ location })).map(
      envData => envData.urls.view,
    ),
    notFoundViewUrl(): string {
      return appUrls.view404;
    },
    REDIRECT_404_LOG_STORAGE_KEY: 'geniallyRedirect404Reason',
    panel404: appUrls.panel404,
    viewLogUrl(): string {
      return appUrls.viewLog;
    },
    editorLogUrl(): string {
      return appUrls.editorLog;
    },
    panelLogUrl(): string {
      return appUrls.panelLog;
    },
    getSocialProfile(nameIdentifier: string, isTeamSocialProfile: boolean): string {
      const subPath = isTeamSocialProfile ? 'ws' : 'profile';
      return `${appUrls.view}/${subPath}/${nameIdentifier}`;
    },
    snowplow: {
      collectorUrl: appUrls.snowplowCollectorUrl,
      appId: 'genially-webapp',
    },
    ltiUrl: appUrls.lti, // lti is included in the object. Just use config.lti instead of config.ltiUrl
  };
};

const config = generateConfig(getHref());

export default config;
